import type { ObservationOrigin } from '@liza/fhir-utils';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import { useTimeXAxisProps } from '../../hooks/useTimeXAxisProps';
import { cn } from '../../utils/cn';
import { normalizeTrendSeries, type TrendPoint } from '../../utils/observation';
import { formatChartDate } from './chartTime';
import { ChartContainer, ChartTooltip, type ChartConfig } from '../ui/chart';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { ALL_SERIES_KEY } from './combinedChartKeys';
import { observationOriginLabel } from './observationOriginLabel';
import { resultVariantLabel } from './resultVariantLabel';
import { provenanceDot } from './ProvenanceDot';

/** One measurement overlaid in the combined view. `key` must be a stable id (e.g. the LOINC). */
export interface CombinedSeries {
  key: string;
  label: string;
  /** The measurement's results-chart color, used when this series is the focused (active) line. */
  color: string;
  unit?: string;
  data: TrendPoint[];
}

export interface CombinedMeasurementChartProps {
  series: CombinedSeries[];
  /** Shared epoch-ms time domain (same as the separated charts), so the combined view aligns with them. */
  domain: [number, number];
  /** Ties this chart into the Results' synchronized hover group. */
  syncId?: string;
  /** Focused series key (controlled). `ALL_SERIES_KEY` lights every line; falls back to internal state. */
  activeKey?: string;
  onActiveKeyChange?: (key: string) => void;
  /** Label for the leading "All" tab. */
  allLabel?: string;
  className?: string;
}

// At this density the connecting spline is redundant, so each series renders as dots only. In "All"
// each keeps its own shade of purple (a gradation); a focused single result stays full while the
// others mute to a neutral gray and dim back.
const CONTEXT_COLOR = 'var(--color-muted-foreground)';
const DIMMED_OPACITY = 0.3;
// A tab-hover preview shows dots in their color but at half strength, so it doesn't compete with the
// fully-opaque selected series.
const HOVER_PREVIEW_OPACITY = 0.5;
const DOT_RADIUS = 2.5;
const ACTIVE_DOT_RADIUS = 4.5;

// Ease the dots' color/opacity change so switching focus doesn't flash jarringly. Targets the dot
// circles (recharts puts the Line's className on the wrapping group, not the dots themselves).
const DOT_TRANSITION_CLASS =
  '[&_.recharts-dot]:transition-[fill,stroke,opacity] [&_.recharts-dot]:duration-200 [&_.recharts-dot]:ease-out';

const rawKey = (key: string): string => `${key}__raw`;
const secondKey = (key: string): string => `${key}__2`;
const provenanceKey = (key: string): string => `${key}__prov`;
const codeKey = (key: string): string => `${key}__code`;
const codeDisplayKey = (key: string): string => `${key}__codeDisplay`;

type TooltipEntry = { payload?: Record<string, unknown> };

/**
 * The "Combine" view of a topic's Results: every measurement overlaid on one shared time axis. Units
 * are incompatible (lbs / mmHg / bpm), so each series is normalized to its own 0–1 range — the chart
 * compares *shape and timing*, not magnitude — and the numeric y-axis is hidden. All lines always
 * render; the tabs (the key) focus one measurement, drawing it in the accent color while the rest
 * recede to context lines. The tooltip shows every metric's true value + unit; magnitudes are read in
 * the stacked view.
 */
export function CombinedMeasurementChart({
  series,
  domain,
  syncId,
  activeKey: controlledKey,
  onActiveKeyChange,
  allLabel = 'All',
  className,
}: CombinedMeasurementChartProps): React.ReactNode {
  const { t } = useTranslation('records');
  const originLabel = useCallback((origin: ObservationOrigin): string => observationOriginLabel(t, origin), [t]);
  const [internalKey, setInternalKey] = useState<string>(ALL_SERIES_KEY);
  const focused = controlledKey ?? internalKey;
  const activeKey = focused === ALL_SERIES_KEY || series.some((s) => s.key === focused) ? focused : ALL_SERIES_KEY;
  const setActive = onActiveKeyChange ?? setInternalKey;
  // "All" shows every line at full strength; otherwise the focused series is full and the rest dim.
  const allMode = activeKey === ALL_SERIES_KEY;
  // Hovering a tab previews its line at full strength even when another is focused.
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  // Merge every series into a row-per-timestamp array. Each series writes its normalized value under
  // its key (for the line) and a formatted raw reading under `<key>__raw` (for the tooltip). Different
  // series co-locate in one row when they share a timestamp (so the tooltip lists them together), but a
  // series with several readings at the *same* timestamp (e.g. multiple steps logged for one day) gets a
  // fresh row per reading rather than overwriting — otherwise all but the last would silently vanish.
  const data = useMemo(() => {
    const rowsByX = new Map<number, Record<string, unknown>[]>();
    for (const s of series) {
      for (const p of normalizeTrendSeries(s.data)) {
        const rows = rowsByX.get(p.x) ?? [];
        let row = rows.find((r) => !(s.key in r));
        if (!row) {
          row = { x: p.x };
          rows.push(row);
        }
        row[s.key] = p.n;
        if (p.n2 !== undefined) row[secondKey(s.key)] = p.n2;
        row[rawKey(s.key)] = p.y2 !== undefined ? `${p.y}/${p.y2}` : String(p.y);
        if (p.provenance) row[provenanceKey(s.key)] = p.provenance.origin;
        if (p.provenance?.source) row[`${provenanceKey(s.key)}__src`] = p.provenance.source;
        if (p.code) row[codeKey(s.key)] = p.code;
        if (p.codeDisplay) row[codeDisplayKey(s.key)] = p.codeDisplay;
        rowsByX.set(p.x, rows);
      }
    }
    return Array.from(rowsByX.values())
      .flat()
      .sort((a, b) => (a.x as number) - (b.x as number));
  }, [series]);

  const config = useMemo<ChartConfig>(
    () => Object.fromEntries(series.map((s) => [s.key, { label: s.label }])),
    [series],
  );

  const renderTooltip = useCallback(
    ({ payload }: { active?: boolean; payload?: readonly TooltipEntry[] }): React.ReactNode => {
      // Reads the single hovered row. When one series has several readings at the same timestamp they
      // live in separate rows (see the merge above), so hovering that cluster surfaces one of them
      // individually rather than aggregating all — an accepted tradeoff of drawing every duplicate dot.
      const row = payload?.[0]?.payload;
      if (!row) return null;
      // Only the active chart's measures that actually have a reading at this point. A single tab thus
      // never shows a date where it has no data (the merged data has a row at every series' timestamp);
      // "All" shows every series with a reading here.
      const shown = series.filter((s) => (allMode || s.key === activeKey) && row[rawKey(s.key)] !== undefined);
      if (shown.length === 0) return null;
      const x = row.x;
      return (
        <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-md">
          {typeof x === 'number' && (
            <div className="mb-1 font-medium text-popover-foreground">{formatChartDate(x)}</div>
          )}
          <div className="grid gap-1">
            {shown.map((s) => {
              const origin = row[provenanceKey(s.key)] as ObservationOrigin | undefined;
              const source = row[`${provenanceKey(s.key)}__src`] as string | undefined;
              const code = row[codeKey(s.key)] as string | undefined;
              // Name the variant only when it differs from the row's own code, so the usual case
              // (a reading coded exactly what the plan tracks) stays a single clean line.
              const variant =
                code && code !== s.key
                  ? resultVariantLabel(code, row[codeDisplayKey(s.key)] as string | undefined)
                  : undefined;
              return (
                <div key={s.key} className="grid gap-0.5">
                  <div className="flex items-center gap-2">
                    <span
                      className="size-2 shrink-0 rounded-[2px]"
                      style={{ backgroundColor: s.color }}
                      aria-hidden="true"
                    />
                    <span className="text-popover-foreground">{s.label}</span>
                    <span className="ml-auto font-medium text-popover-foreground">
                      {String(row[rawKey(s.key)])}
                      {s.unit ? ` ${s.unit}` : ''}
                    </span>
                  </div>
                  {(variant || origin) && (
                    <div className="pl-4 text-muted-foreground">
                      {[variant, origin ? (source ?? originLabel(origin)) : undefined].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    },
    [series, activeKey, allMode, originLabel],
  );

  // Shared time axis with a zoom tween on domain change.
  const xAxisProps = useTimeXAxisProps(domain);

  // Paint order: idle context lines first, then the active line, then a hovered line on very top.
  const ordered = useMemo(() => {
    const rank = (key: string): number => (key === hoveredKey ? 2 : key === activeKey ? 1 : 0);
    return [...series].sort((a, b) => rank(a.key) - rank(b.key));
  }, [series, activeKey, hoveredKey]);

  return (
    <div className="flex flex-col gap-2">
      <Tabs value={activeKey} onValueChange={setActive} className="w-full items-stretch sm:items-center">
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger
            value={ALL_SERIES_KEY}
            className="flex-1 text-xs sm:flex-none"
            onMouseEnter={() => setHoveredKey(null)}
            onMouseLeave={() => setHoveredKey(null)}
          >
            {allLabel}
          </TabsTrigger>
          {series.map((s) => (
            <TabsTrigger
              key={s.key}
              value={s.key}
              className="flex-1 gap-1.5 text-xs sm:flex-none"
              onMouseEnter={() => setHoveredKey(s.key)}
              onMouseLeave={() => setHoveredKey(null)}
            >
              {/* Dot reflects this line's shade — a small color key for the gradation. */}
              <span
                className="size-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: s.color }}
                aria-hidden="true"
              />
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {/* Marker key: what a filled versus a hollow dot means. Color still says which series. */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="size-2 shrink-0 rounded-full bg-muted-foreground" aria-hidden="true" />
          {t('provenanceKey.synced')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-2 shrink-0 rounded-full border-[1.5px] border-muted-foreground" aria-hidden="true" />
          {t('provenanceKey.entered')}
        </span>
      </div>
      <ChartContainer config={config} className={cn('my-4 h-[250px] w-full', className)}>
        <LineChart
          data={data}
          margin={{ top: 8, right: 12, bottom: 8, left: 8 }}
          syncId={syncId}
          syncMethod="value"
          accessibilityLayer
        >
          {/* The default grid uses the semitransparent border token and disappears; muted-foreground at low opacity reads reliably. */}
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-muted-foreground)" strokeOpacity={0.2} />
          {/* Axis hidden — the structural timeline below and the hover tooltip carry the dates. The
              shared time scale, zoom tween, and overflow clipping come from useTimeXAxisProps. */}
          <XAxis {...xAxisProps} hide />
          {/* Hidden because the values are unitless 0–1 normalizations; magnitudes live in the tooltip. */}
          <YAxis hide domain={[0, 1]} />
          {/* No full-height cursor line (it sweeps the grid + every line); the focused line's active dot
              and the tooltip mark the point. Disable the position tween so the tooltip snaps to the cursor. */}
          <ChartTooltip content={renderTooltip} cursor={false} isAnimationActive={false} />
          {ordered.flatMap((s) => {
            // Selected (or every series in "All") → full color; a tab-hover preview → its color at half
            // strength; everything else mutes to gray and dims.
            const active = allMode || s.key === activeKey;
            const hovered = !active && s.key === hoveredKey;
            const color = active || hovered ? s.color : CONTEXT_COLOR;
            const opacity = active ? 1 : hovered ? HOVER_PREVIEW_OPACITY : DIMMED_OPACITY;
            // Dots only (no connecting line), drawn by the provenance marker: filled for a reading
            // synced from a device or arriving from the health record, hollow for one typed in.
            const dotAt = (r: number, o: number): ReturnType<typeof provenanceDot> =>
              provenanceDot({ provenanceKey: provenanceKey(s.key), radius: r, color, opacity: o });
            const hasSecond = s.data.some((p) => p.y2 !== undefined);
            const lines = [
              <Line
                key={s.key}
                dataKey={s.key}
                name={s.label}
                stroke="none"
                dot={dotAt(DOT_RADIUS, opacity)}
                activeDot={dotAt(ACTIVE_DOT_RADIUS, opacity)}
                isAnimationActive={false}
                className={DOT_TRANSITION_CLASS}
              />,
            ];
            // Composite readings (e.g. BP diastolic) draw a second set of dots, subordinate to the first.
            if (hasSecond) {
              lines.push(
                <Line
                  key={secondKey(s.key)}
                  dataKey={secondKey(s.key)}
                  stroke="none"
                  dot={dotAt(DOT_RADIUS, opacity * 0.6)}
                  activeDot={dotAt(ACTIVE_DOT_RADIUS, opacity * 0.6)}
                  isAnimationActive={false}
                  className={DOT_TRANSITION_CLASS}
                />,
              );
            }
            return lines;
          })}
        </LineChart>
      </ChartContainer>
    </div>
  );
}
